import { Locale } from "@/Common/Models/Localization/Locale";

class InlineOption<T> {
    public Key: T;
    public Value: React.ReactNode;
    public Disabled?: boolean;

    constructor(key: T, value: React.ReactNode, disabled?: boolean) {
        this.Key = key;
        this.Value = value;
        this.Disabled = disabled;
    }
}

interface IAdditionalElement {
    id: string,
    element: React.ReactNode
}

interface BaseInlineCheckboxListProps<T> {
    id: string;
    style?: React.CSSProperties;
    inputStyle?: React.CSSProperties;
    disabled?: boolean;
    additionalElements?: IAdditionalElement[];
    optionSpacing?: string;
    boldLabels?: boolean;
    wrapOptions?: boolean;
    isMultipleLines?: boolean;
    items?: InlineOption<T>[]
    tabIndex?: number;
    className?: string;
    onBlur?: () => void;
    additionalWrapperClassName?: string;
    heightInPixels?: number;
    labelStyle?: React.CSSProperties;
}

abstract class BaseInlineListControl<TProps extends BaseInlineCheckboxListProps<T>, T> extends React.Component<TProps, never> {
    public render(): JSX.Element {
        const heightInPixels = this.props.heightInPixels;

        return (
            <div
                className={`${this.props.isMultipleLines ? "" : "inline-selection"} ${this.props.wrapOptions ? "flex-wrap" : ""} ${this.props.additionalWrapperClassName ?? ""}`}
                style={{
                    height: `${heightInPixels}px`,
                    columnGap: this.props.optionSpacing ?? "15px"
                }}
            >
                {this.props.items?.map(item => (
                    <div
                        className={(!item.Value && this.props.className) ? this.props.className : ""}
                        style={{
                            margin: "0",
                            display: "flex",
                            alignItems: "center",
                            columnGap: "5px",
                            ...this.props.style
                        }}
                        key={this.props.id + item.Key}
                    >
                        <input
                            id={this.props.id + "_" + item.Key}
                            type={this.getInputType()}
                            onChange={() => null} /* Avoid react warning. `checked` prop to a form field without an `onChange` */
                            onClick={() => !this.props.disabled && this.handleClick(item)}
                            checked={this.isChecked(item)}
                            disabled={this.props.disabled || (this.isChecked(item) && this.props.items.filter(p => item !== p).all(p => p.Disabled))}
                            style={{
                                cursor: (this.props.disabled || item.Disabled) ? "not-allowed" : "pointer",
                                margin: 0,
                                ...this.props.inputStyle
                            }}
                            name={`${this.props.id}_${item.Key}`}
                            tabIndex={this.props.tabIndex}
                            onBlur={this.props.onBlur}
                        />
                        {item.Value && (
                            <label
                                htmlFor={`${this.props.id}_${item.Key}`}
                                style={{
                                    fontWeight: this.props.boldLabels ? "bold" : "normal",
                                    margin: 0,
                                    cursor: (this.props.disabled || item.Disabled) ? "not-allowed" : "pointer",
                                    ...this.props.labelStyle
                                }}
                            >
                                {item.Value}
                            </label>
                        )}
                    </div>
                ))}
                {this.props.additionalElements &&
                    this.props.additionalElements.map(x => <div key={x.id} style={{ marginRight: "15px", display: "inline-block", ...this.props.style }}>{x.element}</div>)
                }
            </div>
        );
    }

    protected abstract getInputType(): string;

    protected abstract isChecked(item: InlineOption<T>): boolean;

    protected abstract handleClick(item: InlineOption<T>): void;
}

interface InlineCheckboxListProps<T> extends BaseInlineCheckboxListProps<T> {
    values: T[];
    onChange?: (options: T[]) => void;
}

class InlineCheckboxListControl<T> extends BaseInlineListControl<InlineCheckboxListProps<T>, T> {
    protected getInputType(): string {
        return "checkbox";
    }

    protected isChecked(item: InlineOption<T>): boolean {
        return this.props.values.contains(item.Key);
    }

    protected handleClick(item: InlineOption<T>): void {
        let values = [...this.props.values];
        if (this.isChecked(item))
            values = values.filter(value => value !== item.Key);
        else
            values.push(item.Key);

        this.props.onChange?.(values);
    }
}

interface InlineOptionsListProps<T> extends BaseInlineCheckboxListProps<T> {
    value: T;
    onChange: (option: InlineOption<T>) => void;
    allowDeselect?: boolean;
}

class InlineOptionsListControl<T> extends BaseInlineListControl<InlineOptionsListProps<T>, T> {
    public static YesOrNo(props: InlineOptionsListProps<boolean> & { abbreviate?: boolean, locale?: Locale; reverseOrder?: boolean }): JSX.Element {
        return (
            <InlineOptionsListControl
                {...props}
                items={InlineOptionsListControl.getYesOrNoOptions(props.locale, props.abbreviate, props?.reverseOrder)}
            />
        );
    }

    public static getYesOrNoOptions(locale?: Locale, abbreviate?: boolean, reverseOrder?: boolean): InlineOption<boolean>[] {
        let yes = cacheStore.GetString("YesLabel", null, locale);
        let no = cacheStore.GetString("NoLabel", null, locale);

        if (abbreviate) {
            yes = yes.substr(0, 1);
            no = no.substr(0, 1);
        }

        const options = [
            new InlineOption<boolean>(true, yes),
            new InlineOption<boolean>(false, no)
        ];

        return reverseOrder ? options.reverse() : options;
    }

    protected getInputType(): string {
        return this.props.allowDeselect ? "checkbox" : "radio";
    }

    protected isChecked(item: InlineOption<T>): boolean {
        return item.Key === this.props.value;
    }

    protected handleClick(item: InlineOption<T>): void {
        if (item.Disabled)
            return;

        const deselect = this.props.allowDeselect && this.isChecked(item);
        this.props.onChange(deselect ? null : item);
    }
}

export { IAdditionalElement, InlineCheckboxListControl, InlineOption, InlineOptionsListControl };
